import * as Tone from 'tone';

// --- Configuration ---
const BPM = 132;
const ROOT_NOTE = "D2"; // D Minor
const SCALE = ["D2", "F2", "G2", "A2", "C3", "D3", "F3", "G3"]; // D Minor Pentatonicish

// --- State ---
let isInitialized = false;
let currentPhase = 'intro'; // intro, play, mid, high, gameover
let musicBus = null;
let limiter = null;

// --- Instruments ---
let kick = null;
let snare = null;
let hats = null;
let bass = null;
let pad = null;
let arp = null;

// --- Sequences ---
let kickSeq = null;
let snareSeq = null;
let hatsSeq = null;
let bassSeq = null;
let padSeq = null;
let arpSeq = null;

// --- Patterns ---
const PATTERNS = {
    kick: ["C1", null, "C1", null], // 4-on-floor (simplified for 4n)
    snare: [null, "4n", null, "4n"], // Backbeat
    hats: [
        "16n", "16n", "16n", "16n",
        "16n", "16n", "16n", "16n",
        "16n", "16n", "16n", "16n",
        "16n", "16n", "16n", "16n"
    ],
    bass: [
        "D2", null, "D2", null,
        "F2", null, "D2", null,
        "G2", null, "F2", null,
        "A2", null, "C3", null
    ],
    arp: [
        "D4", "F4", "A4", "D5",
        "F5", "D5", "A4", "F4",
        "G4", "Bb4", "D5", "G5",
        "F5", "D5", "Bb4", "G4"
    ],
    pad: [
        ["D3", "F3", "A3"], // Dm
        ["Bb2", "D3", "F3"], // Bb
        ["C3", "E3", "G3"], // C
        ["A2", "C3", "E3"]  // Am
    ]
};

export const initMusic = () => {
    if (isInitialized) return;

    // 1. Master Bus & Limiter
    limiter = new Tone.Limiter(-1).toDestination();
    musicBus = new Tone.Gain(0.5).connect(limiter); // Master Music Volume (-6dB approx)

    // 2. Instruments

    // Kick: Punchy, tight
    kick = new Tone.MembraneSynth({
        pitchDecay: 0.05,
        octaves: 4,
        oscillator: { type: "sine" },
        envelope: { attack: 0.001, decay: 0.4, sustain: 0.01, release: 1.4 }
    }).connect(musicBus);
    kick.volume.value = -6;

    // Snare: Noise + Tone
    snare = new Tone.NoiseSynth({
        noise: { type: "white" },
        envelope: { attack: 0.001, decay: 0.2, sustain: 0 }
    }).connect(musicBus);
    snare.volume.value = -12;

    // Hats: Metallic
    hats = new Tone.MetalSynth({
        frequency: 200,
        envelope: { attack: 0.001, decay: 0.1, release: 0.01 },
        harmonicity: 5.1,
        modulationIndex: 32,
        resonance: 4000,
        octaves: 1.5
    }).connect(musicBus);
    hats.volume.value = -20;

    // Bass: Mono Synth with Filter
    bass = new Tone.MonoSynth({
        oscillator: { type: "sawtooth" },
        filter: { Q: 2, type: "lowpass", rollover: -12 },
        envelope: { attack: 0.005, decay: 0.1, sustain: 0.4, release: 0.1 },
        filterEnvelope: { attack: 0.001, decay: 0.1, sustain: 0.2, baseFrequency: 200, octaves: 2.5 }
    }).connect(musicBus);
    bass.volume.value = -10;

    // Pad: PolySynth with Chorus
    const chorus = new Tone.Chorus(4, 2.5, 0.5).connect(musicBus).start();
    pad = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "triangle" },
        envelope: { attack: 1, decay: 1, sustain: 0.8, release: 2 }
    }).connect(chorus);
    pad.volume.value = -18;

    // Arp: PolySynth with Delay
    const delay = new Tone.PingPongDelay("8n", 0.2).connect(musicBus);
    arp = new Tone.PolySynth(Tone.Synth, {
        oscillator: { type: "sawtooth" },
        envelope: { attack: 0.01, decay: 0.1, sustain: 0.1, release: 0.1 }
    }).connect(delay);
    arp.volume.value = -20;

    // 3. Sequences
    Tone.Transport.bpm.value = BPM;

    // Kick (4-on-floor)
    kickSeq = new Tone.Sequence((time, note) => {
        if (note) kick.triggerAttackRelease(note, "8n", time);
    }, ["C1", null, "C1", null], "4n").start(0);

    // Snare (Backbeat)
    snareSeq = new Tone.Sequence((time, note) => {
        if (note) snare.triggerAttackRelease("8n", time);
    }, [null, "hit", null, "hit"], "4n").start(0);

    // Hats (16ths)
    hatsSeq = new Tone.Sequence((time, note) => {
        if (note) hats.triggerAttackRelease("32n", time, Math.random() * 0.5 + 0.5);
    }, PATTERNS.hats, "16n").start(0);

    // Bass (16ths)
    bassSeq = new Tone.Sequence((time, note) => {
        if (note) bass.triggerAttackRelease(note, "16n", time);
    }, PATTERNS.bass, "16n").start(0);

    // Pad (Whole notes / Chords)
    padSeq = new Tone.Sequence((time, notes) => {
        if (notes) pad.triggerAttackRelease(notes, "1m", time);
    }, PATTERNS.pad, "1m").start(0);

    // Arp (16ths)
    arpSeq = new Tone.Sequence((time, note) => {
        if (note) arp.triggerAttackRelease(note, "16n", time);
    }, PATTERNS.arp, "16n").start(0);

    // Mute all initially
    muteAll();

    isInitialized = true;
};

const muteAll = () => {
    if (!isInitialized) return;
    kickSeq.mute = true;
    snareSeq.mute = true;
    hatsSeq.mute = true;
    bassSeq.mute = true;
    padSeq.mute = true;
    arpSeq.mute = true;
};

export const startMusic = async () => {
    if (!isInitialized) initMusic();
    await Tone.start();
    Tone.Transport.start();
    setMusicPhase('play'); // Start with basic beat
};

export const stopMusic = () => {
    if (!isInitialized) return;
    // Fade out?
    Tone.Transport.stop();
    muteAll();
    currentPhase = 'intro';
};

export const setMusicPhase = (phase) => {
    if (!isInitialized || phase === currentPhase) return;
    currentPhase = phase;

    // Reset mutes based on phase
    // intro: Silence / Pad only
    // play: Kick + Bass
    // mid: Kick + Bass + Hats + Snare + Pad
    // high: All (Arp added)
    // gameover: Mute all

    switch (phase) {
        case 'intro':
            muteAll();
            padSeq.mute = false; // Just pads
            break;
        case 'play':
            muteAll();
            kickSeq.mute = false;
            bassSeq.mute = false;
            break;
        case 'mid':
            muteAll();
            kickSeq.mute = false;
            bassSeq.mute = false;
            hatsSeq.mute = false;
            snareSeq.mute = false;
            padSeq.mute = false;
            break;
        case 'high':
            muteAll();
            kickSeq.mute = false;
            bassSeq.mute = false;
            hatsSeq.mute = false;
            snareSeq.mute = false;
            padSeq.mute = false;
            arpSeq.mute = false;
            break;
        case 'gameover':
            muteAll();
            // Maybe just pads for a bit?
            padSeq.mute = false;
            break;
        default:
            break;
    }
};

export const setVolume = (db) => {
    if (musicBus) musicBus.gain.value = db; // This might need conversion if gain is linear
    // Tone.Gain takes linear (0-1) or we can use decibels on instruments.
    // Let's assume input is 0-1 for simplicity
    if (musicBus) musicBus.gain.rampTo(db, 0.1);
};

export const cleanupMusic = () => {
    if (!isInitialized) return;

    // Dispose Sequences
    [kickSeq, snareSeq, hatsSeq, bassSeq, padSeq, arpSeq].forEach(seq => seq?.dispose());
    kickSeq = snareSeq = hatsSeq = bassSeq = padSeq = arpSeq = null;

    // Dispose Instruments
    [kick, snare, hats, bass, pad, arp].forEach(inst => inst?.dispose());
    kick = snare = hats = bass = pad = arp = null;

    // Dispose Bus
    musicBus?.dispose();
    limiter?.dispose();
    musicBus = null;
    limiter = null;

    isInitialized = false;
};
